<script setup>
import FormStepper from '@/components/forms/FormStepper.vue'
import { useValidationFieldErrors } from '@/composables/useValidationFieldErrors'
import { useValidationStore } from '@/stores/validation'
import { $api } from '@/utils/api'
import SurgeonSecretaryProfileDetails from '@/views/pages/surgeon_secretary/SurgeonSecretaryProfileDetails.vue'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const validationStore = useValidationStore()
const router = useRouter()

const currentStep = ref(0)
const loading = ref(false)
const registrationComplete = ref(false)
const accountCreated = ref(false)
const userEmail = ref('')

const registrationData = ref({
  first_name: '',
  last_name: '',
  email: '',
  subscription_tier: 'standard',
})

const profileData = ref({
  surgeons: [
    { first_name: '', last_name: '', gmc_number: '' },
  ],
})


const steps = [
  {
    title: 'Account',
    subtitle: 'Create your account',
    icon: 'ri-shield-user-line',
    required: true,
  },
  {
    title: 'Surgeons',
    subtitle: 'Associate surgeons',
    icon: 'ri-team-line',
    required: false,
  },
]

const skipMessage = computed(() => '')

const { fieldError, fieldErrorMessage, clearErrorsFor } = useValidationFieldErrors(validationStore)

const canProceedFromAccount = computed(() => {
  const hasAccount = registrationData.value.email

  return hasAccount && registrationData.value.first_name && registrationData.value.last_name
})

const goToNextStep = () => {
  if (currentStep.value === 0 && !canProceedFromAccount.value) {
    const errors = {}

    if (!registrationData.value.first_name)
      errors.first_name = ['First name is required.']

    if (!registrationData.value.last_name)
      errors.last_name = ['Last name is required.']

    if (!registrationData.value.email) {
      errors.email = ['Email is required.']
    }
    else if (!/.+@.+\..+/.test(String(registrationData.value.email))) {
      errors.email = ['Enter a valid email address.']
    }

    validationStore.setErrors({
      message: 'Please complete the required fields before continuing.',
      errors,
    })

    return
  }

  validationStore.clearErrors()
  if (currentStep.value < steps.length - 1)
    currentStep.value++
}

const goToPreviousStep = () => {
  validationStore.clearErrors()
  if (currentStep.value > 0)
    currentStep.value--
}

const submitAccountCreation = async () => {
  loading.value = true
  validationStore.clearErrors()

  try {
    const payload = {
      email: registrationData.value.email,
      subscription_tier: registrationData.value.subscription_tier,
      first_name: registrationData.value.first_name,
      last_name: registrationData.value.last_name,
    }

    await $api('/api/surgeon-secretaries', {
      method: 'POST',
      body: payload,
    })

    accountCreated.value = true
    userEmail.value = registrationData.value.email
  }
  catch (err) {
    const payload = err?.response?._data ?? err?.data ?? {}

    validationStore.setErrors({
      message: payload?.message ?? 'Registration failed. Please check your information.',
      errors: payload?.errors || {},
    })
  }
  finally {
    loading.value = false
  }
}

const submitProfileData = async () => {
  loading.value = true
  validationStore.clearErrors()

  try {
    const surgeons = (profileData.value.surgeons || [])
      .filter(surgeon => surgeon.first_name && surgeon.last_name)
      .map(surgeon => ({
        first_name: surgeon.first_name,
        last_name: surgeon.last_name,
        gmc_number: surgeon.gmc_number || null,
      }))

    if (surgeons.length) {
      await $api(`/api/surgeon-secretaries/by-email/${userEmail.value}/surgeons`, {
        method: 'POST',
        body: { surgeons },
      })
    }

    registrationComplete.value = true
    await router.push('/users/register-thank-you')
  }
  catch (err) {
    const payload = err?.response?._data ?? err?.data ?? {}

    validationStore.setErrors({
      message: payload?.message ?? 'Failed to update profile.',
      errors: payload?.errors || {},
    })
  }
  finally {
    loading.value = false
  }
}

const submitRegistration = async () => {
  if (currentStep.value === 0) {
    await submitAccountCreation()
    if (accountCreated.value)
      currentStep.value++
  }
  else if (currentStep.value === 1) {
    await submitProfileData()
  }
}

const isCurrentStepOptional = computed(() => currentStep.value > 0 && !steps[currentStep.value]?.required)

const skipToComplete = () => {
  registrationComplete.value = true
  router.push('/users/register-thank-you')
}

const handleBack = () => {
  if (currentStep.value === 0) {
    router.push({ name: 'login' })
    
    return
  }

  goToPreviousStep()
}

watch(currentStep, () => {
  validationStore.clearErrors()
})
</script>

<template>
  <div class="auth-wrapper d-flex">
    <VCol
      md="4"
      class="d-none d-md-flex align-top justify-center pa-10"
      style="background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, rgb(var(--v-theme-primary-darken-1)) 100%);"
    >
      <div class="text-center text-white">
        <img
          src="/logo-white-trasp.png"
          width="140"
          height="140"
          class="brand-logo mb-8 mx-auto d-block"
          alt="Starter Kit logo"
        >
        <h2 class="text-h3 font-weight-bold mb-4">
          Surgeon Secretary Registration
        </h2>
        <p class="text-h6 mb-8">
          Complete your onboarding to manage listings, coordinate surgeons, and connect with anaesthetists across the UK.
        </p>
        <div class="d-flex flex-column gap-4 text-left">
          <div class="d-flex align-center gap-3">
            <VIcon
              icon="ri-check-line"
              size="24"
            />
            <span>Centralise surgeon communications</span>
          </div>
          <div class="d-flex align-center gap-3">
            <VIcon
              icon="ri-check-line"
              size="24"
            />
            <span>Manage listings and availability</span>
          </div>
          <div class="d-flex align-center gap-3">
            <VIcon
              icon="ri-check-line"
              size="24"
            />
            <span>Stay in sync with theatre teams</span>
          </div>
          <div class="d-flex align-center gap-3">
            <VIcon
              icon="ri-check-line"
              size="24"
            />
            <span>Secure access to your network</span>
          </div>
        </div>
      </div>
    </VCol>

    <VCol
      cols="12"
      md="8"
      class="d-flex align-top justify-center"
      style="background-color: rgb(var(--v-theme-surface));"
    >
      <VCard
        flat
        class="registration-card"
        :elevation="0"
      >
        <div class="d-flex align-top justify-center mb-8">
          <RouterLink to="/">
            <div class="d-flex align-center gap-3">
              <VNodeRenderer :nodes="themeConfig.app.logo" />
              <h1 class="text-h4">
                {{ themeConfig.app.title }}
              </h1>
            </div>
          </RouterLink>
        </div>

        <VExpandTransition>
          <div v-if="registrationComplete">
            <VCard
              class="pa-8 completion-card"
              elevation="0"
            >
              <div class="completion-content">
                <VIcon
                  icon="ri-checkbox-circle-line"
                  size="56"
                  color="success"
                  class="mb-4"
                />

                <p class="mb-3 text-h5 font-weight-bold text-center">
                  Thank you for your registration
                </p>

                <p class="mb-3 text-body-1 text-medium-emphasis text-center">
                  Your request has been received successfully.
                </p>

                <p class="mb-6 text-body-1 text-medium-emphasis text-center">
                  Our staff will review and activate your account within 48 hours.
                </p>

                <VBtn
                  color="primary"
                  size="large"
                  href="/"
                  prepend-icon="ri-home-4-line"
                >
                  Back to institutional site
                </VBtn>
              </div>
            </VCard>
          </div>
        </VExpandTransition>

        <VExpandTransition>
          <VAlert
            v-if="Object.keys(validationStore.errors).length"
            type="error"
            variant="tonal"
            class="mb-6"
            closable
            @click:close="validationStore.clearErrors()"
          >
            {{ validationStore.message || 'Please correct the errors below' }}
          </VAlert>
        </VExpandTransition>

        <div v-if="!registrationComplete">
          <FormStepper
            :current-step="currentStep"
            :steps="steps"
            :loading="loading"
            :is-optional="isCurrentStepOptional"
            :skip-message="skipMessage"
            :fixed-navigation="true"
            back-label="Back"
            next-label="Next"
            complete-label="Complete Registration"
            skip-label="Skip"
            @update:current-step="currentStep = $event"
            @back="handleBack"
            @next="submitRegistration"
            @skip="skipToComplete"
          >
            <VWindow
              v-model="currentStep"
              class="disable-tab-transition"
            >
              <VWindowItem :value="0">
                <VCard>
                  <VCardText>
                    <div class="text-h6 mb-6">
                      Account Information
                    </div>
                    <VRow>
                      <VCol
                        cols="12"
                        md="6"
                      >
                        <VTextField
                          v-model="registrationData.first_name"
                          label="First name"
                          autocomplete="noperc"
                          placeholder="Enter your first name"
                          variant="outlined"
                          prepend-inner-icon="ri-user-line"
                          :error="fieldError('first_name')"
                          :error-messages="fieldErrorMessage('first_name')"
                          required
                          @focus="clearErrorsFor('first_name')"
                        />
                      </VCol>

                      <VCol
                        cols="12"
                        md="6"
                      >
                        <VTextField
                          v-model="registrationData.last_name"
                          label="Last name"
                          autocomplete="noperc"
                          placeholder="Enter your last name"
                          variant="outlined"
                          prepend-inner-icon="ri-user-line"
                          :error="fieldError('last_name')"
                          :error-messages="fieldErrorMessage('last_name')"
                          required
                          @focus="clearErrorsFor('last_name')"
                        />
                      </VCol>

                      <VCol cols="12">
                        <VTextField
                          v-model="registrationData.email"
                          label="Email address"
                          autocomplete="noperc"
                          placeholder="your.email@example.com"
                          type="email"
                          variant="outlined"
                          prepend-inner-icon="ri-mail-line"
                          :error="fieldError('email')"
                          :error-messages="fieldErrorMessage('email')"
                          required
                          @focus="clearErrorsFor('email')"
                        />
                      </VCol>
                      <VCol cols="12">
                        <VAlert
                          type="info"
                          variant="tonal"
                        >
                          We will email you a secure link to confirm your email address and choose a password.
                        </VAlert>
                      </VCol>
                    </VRow>
                  </VCardText>
                </VCard>
              </VWindowItem>

              <VWindowItem :value="1">
                <div class="d-flex flex-column gap-6">
                  <VAlert
                    type="info"
                    variant="tonal"
                    class="optional-step-alert"
                  >
                    <div class="optional-step-alert-content">
                      <span class="text-body-2 text-medium-emphasis">
                        This step is optional. By selecting hospitals, you'll see job listings specifically related to your selected hospitals. You can add more later.
                      </span>
                      <VBtn
                        variant="outlined"
                        color="secondary"
                        :disabled="loading"
                        @click="skipToComplete"
                      >
                        <VIcon
                          icon="ri-skip-right-line"
                          end
                        />
                        Skip
                      </VBtn>
                    </div>
                  </VAlert>

                  <SurgeonSecretaryProfileDetails
                    v-model="profileData"
                    :show-personal-data="false"
                    :show-contacts="false"
                    show-surgeons
                  />
                </div>
              </VWindowItem>
            </VWindow>
          </FormStepper>
        </div>
      </VCard>
    </VCol>
  </div>
</template>

<style scoped>
.brand-logo {
  object-fit: contain;
  object-position: center;
  max-width: 100%;
}
</style>

<style lang="scss" scoped>
.auth-wrapper {
  min-height: 100vh;
}

.registration-card {
  inline-size: 100%;
  max-inline-size: 100%;
  padding-block-end: 110px;
}

.completion-card {
  display: flex;
  justify-content: center;
}

.completion-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-inline-size: 38rem;
  margin-inline: auto;
}


.disable-tab-transition {
  overflow: visible !important;
}

.review-alert-reference {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 12px 16px;
  border-radius: 8px;
  color: rgb(var(--v-theme-info));
  background: rgba(var(--v-theme-info), 0.12);
}

.review-alert-reference .review-alert-icon {
  color: rgb(var(--v-theme-info));
}

@media (max-width: 959px) {
  .registration-card {
    padding-block-end: 170px;
  }
}

.optional-step-alert-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
</style>
